import React from 'react';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import type { AIAuthorityCompetitorComparison } from '../../lib/api-types';

// ── Self-contained helpers (shared by the deep-dive Section + the modal) ──

function fmt(n?: number | null, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return (Math.round(n * 10 ** digits) / 10 ** digits).toString();
}

function scoreTone(score?: number) {
  if (score === null || score === undefined) return 'text-gray-500';
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-blue-700';
  if (score >= 40) return 'text-amber-700';
  return 'text-red-700';
}

function scoreBar(score?: number) {
  if (score === null || score === undefined) return 'bg-gray-300';
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-blue-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

function threatTone(level?: string) {
  const s = (level || '').toLowerCase();
  if (s.includes('high') || s.includes('crit')) return { badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('med')) return { badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' };
  if (s.includes('low')) return { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' };
  return { badge: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
}

const clamp = (n?: number) => Math.max(0, Math.min(100, n ?? 0));

const ScoreBar: React.FC<{ score?: number; benchmark?: number }> = ({ score, benchmark }) => (
  <div className="relative w-full bg-gray-100 rounded-full h-2.5">
    <div className={`h-2.5 rounded-full ${scoreBar(score)} transition-all`} style={{ width: `${clamp(score)}%` }} />
    {benchmark !== undefined && (
      <div
        className="absolute top-1/2 -translate-y-1/2 h-4 w-0.5 bg-gray-500/70 rounded"
        style={{ left: `${clamp(benchmark)}%` }}
        title={`Your brand ${fmt(benchmark)}`}
      />
    )}
  </div>
);

// ── Component ─────────────────────────────────────────────────────────

interface CompetitiveLandscapeProps {
  data: AIAuthorityCompetitorComparison;
}

const CompetitiveLandscape: React.FC<CompetitiveLandscapeProps> = ({ data }) => {
  const competitors = data.competitors ?? [];

  return (
    <div>
      {data.brand_base_score !== undefined && (
        <p className="mb-4 text-sm text-gray-600">
          Your brand&apos;s base score: <span className="font-bold text-[#161463]">{fmt(data.brand_base_score)}</span>
          {data.competitor_count !== undefined ? ` · ${data.competitor_count} competitors tracked` : ''}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {competitors.map((c, i) => {
          const threat = threatTone(c.threat_level);
          const cScore = c.adjusted_score ?? c.score;
          const strengths = c.top_factors?.strengths ?? [];
          const weaknesses = c.top_factors?.weaknesses ?? [];
          return (
            <div key={c.name ?? i} className="rounded-xl border border-gray-100 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{c.name || 'Competitor'}</span>
                  {c.grade && <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-bold bg-[#161463] text-white">{c.grade}</span>}
                </div>
                {c.threat_level && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${threat.badge}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${threat.dot}`} />{c.threat_level}
                  </span>
                )}
              </div>
              {cScore !== undefined && (
                <div className="mt-2 flex items-center gap-2">
                  <span className={`text-sm font-semibold w-10 ${scoreTone(cScore)}`}>{fmt(cScore)}</span>
                  <div className="flex-1"><ScoreBar score={cScore} benchmark={data.brand_base_score} /></div>
                </div>
              )}
              {(strengths.length > 0 || weaknesses.length > 0) && (
                <div className="mt-3 space-y-1.5">
                  {strengths.slice(0, 3).map((f, j) => (
                    <div key={`s${j}`} className="flex items-center gap-1.5 text-xs text-emerald-700"><TrendingUpIcon className="h-3 w-3 flex-shrink-0" />{f.factor}{f.score !== undefined ? ` · ${fmt(f.score)}` : ''}</div>
                  ))}
                  {weaknesses.slice(0, 3).map((f, j) => (
                    <div key={`w${j}`} className="flex items-center gap-1.5 text-xs text-red-500"><TrendingDownIcon className="h-3 w-3 flex-shrink-0" />{f.factor}{f.score !== undefined ? ` · ${fmt(f.score)}` : ''}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.brand_base_score !== undefined && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-400"><span className="inline-block h-3 w-0.5 bg-gray-500/70 rounded" /> marker shows your brand&apos;s base score</p>
      )}
    </div>
  );
};

export default CompetitiveLandscape;
